<template>
    <div class="header-topnav">
        <div class="top-inner">
            <div class="logo-container">
                <a-dynamic-link
                    class="logo"
                    :to="logo.link"
                    :target="logo.target"
                    :rel="logo.rel"
                    :event="{ action: 'Acronis' }"
                >
                    <a-logo type="full" fill="white" />
                </a-dynamic-link>
            </div>

            <div class="nav">
                <div class="menu">
                    <a-dynamic-link
                        v-for="link in menuLinks"
                        :key="link.link"
                        class="link"
                        :to="link.link"
                        :event="{ action: link.text }"
                    >
                        {{ link.text }}
                    </a-dynamic-link>
                </div>
            </div>

            <div class="buttons">
                <div class="menu-burger" @click="menuIsOpened = !menuIsOpened">
                    <template v-if="!menuIsOpened">
                        <span>{{ menuOpenTitle }}</span>
                        <a-glyph
                            class="glyph"
                            name="burger-menu"
                            fill="white"
                            size="l"
                        />
                    </template>
                    <template v-else>
                        <span>{{ menuCloseTitle }}</span>
                        <a-glyph
                            class="glyph"
                            name="i--delete-ab--32"
                            fill="white"
                            size="l"
                        />
                    </template>
                </div>
                <a-button
                    v-for="button in buttons"
                    :key="button.link"
                    class="custom-button"
                    v-bind="button"
                    :to="button.link"
                    :size="button.size || 'medium'"
                    :event="{ category: 'Button click', action: button.text, label: 'top menu' }"
                />
            </div>
        </div>
        <div v-if="menuIsOpened" class="mobile-menu">
            <a-dynamic-link
                v-for="link in menuLinks"
                :key="link.link"
                class="mobile-link"
                :to="link.link"
                :event="{ action: link.text }"
            >
                {{ link.text }}
            </a-dynamic-link>
            <a-button
                v-for="button in buttons"
                :key="button.link"
                class="mobile-button"
                v-bind="button"
                :to="button.link"
                :size="button.size || 'medium'"
                :event="{ category: 'Button click', action: button.text, label: 'top menu' }"
            />
        </div>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import ADynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALogo from '@core/components/logo/logo.vue';

export default {
    name: 'SolutionsHeaderTopNav',

    components: {
        AButton,
        ADynamicLink,
        ALogo,
        AGlyph,
    },

    props: {
        logo: {
            type: Object,
            required: true,
        },
        menuLinks: {
            type: Array,
            required: true,
        },
        buttons: {
            type: Array,
            default: () => [],
        },
        menuOpenTitle: {
            type: String,
            required: true,
        },
        menuCloseTitle: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            menuIsOpened: false,
        };
    },
};
</script>

<style lang="postcss" scoped>
.header-topnav {
    position: relative;
    z-index: 6;
    padding: 24px 0;
    box-sizing: border-box;
}

.top-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    @media (--viewport-desktop-wide) {
        justify-content: flex-start;
    }
}

.logo {
    height: 32px;
    width: 158px;
}

.nav {
    margin-inline-start: auto;
    padding-inline-start: 0;
    padding-inline-end: 16px;
    @media (--viewport-desktop-wide) {
        padding-inline-end: 44px;
    }
}

.menu {
    display: none;
    position: absolute;
    top: 100%;
    inset-inline-start: 0;
    width: 100%;
    height: 0;
    background: var(--av-inversed-primary);

    @media (--viewport-desktop) {
        position: static;
        height: auto !important;
        background: rgba(255, 255, 255, 0);
        display: flex;
        width: auto;
        margin-inline-end: auto;
    }
}

.link {
    @mixin paragraph-accent;
    text-decoration: none;
    padding: 0 8px;
    color: var(--av-inversed-primary);

    &:hover,
    &:active {
        color: var(--av-link-inversed);
    }

    @media (--viewport-desktop-wide) {
        padding: 0 12px;
    }
}

.buttons {
    display: none;
    display: flex;
    align-items: center;
}

.custom-button {
    display: none;
    @media (--viewport-mobile-wide) {
        display: block;
    }
}

.buttons .custom-button:not(:last-child) {
    margin-inline-end: 16px;
}

.custom-button {
    border: 1px solid var(--av-brand-secondary);
    background: transparent;
}

.custom-button.a-button_type_main:hover {
    background-color: var(--av-button-main-hover);
    border-color: var(--av-button-main-hover);
}

.custom-button.a-button_type_action:hover {
    background-color: var(--av-fixed-success);
    border-color: var(--av-fixed-success);
}

.menu-burger {
    display: flex;
    align-items: center;

    .a-glyph {
        margin-inline-start: 8px;
    }

    @media (--viewport-mobile-wide) {
        margin-inline-end: 24px;
    }

    @media (--viewport-desktop) {
        display: none;
    }
}

.mobile-menu {
    background: var(--av-inversed-primary);
    position: absolute;
    top: 80px;
    inset-inline: -16px;
    min-height: calc(100vh - 80px);
    box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.30);
    padding: 8px 32px;

    @media (--viewport-tablet) {
        inset-inline: -32px;
    }

    @media (--viewport-desktop) {
        display: none;
    }
}

.mobile-link {
    @mixin lead-accent;
    display: block;
    color: var(--av-nav-primary);
    padding: 24px 0;

    &:not(:first-child) {
        border-top: 1px solid var(--av-brand-secondary-light);
    }
}

.mobile-button {
    border: 1px solid var(--av-button-inversed);
    background: transparent;
    color: var(--av-nav-primary);
    width: 100%;
    margin-top: 56px;

    @media (--viewport-mobile-wide) {
        display: none;
    }
}
</style>
